import { MDBCol, MDBContainer, MDBIcon, MDBRow } from "mdbreact";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button/Button";
import handleCurrency from "../../../components/CurrencyFormat";
import DataTable from "../../../components/DataTable/DataTable";
import WaitingForm from "../../../components/Forms/WaitingForm";
import Loading from "../../../components/Loading";
import axiosGet from "../../../helpers/axiosGet";
import {
  setDataSummary,
  setInvoiceNumbers,
  setInvoiceTotal,
} from "../../../store/summary/actions";
import ReactPaginate from "react-paginate";
import { useHistory } from "react-router-dom";

const invoiceUrl = (page, values, count) => {
  const params = new URLSearchParams({
    insured: values?.insured || "",
    companyName: values?.companyName || "",
    invoiceNumber: values?.invoiceNumber || "",
    placingSlipNo: values?.placingSlipNo || "",
    productCode: values?.productCode || "",
    minSumInsured: values?.minSumInsured || "",
    maxSumInsured: values?.maxSumInsured || "",
    minPremium: values?.minPremium || "",
    maxPremium: values?.maxPremium || "",
    minPolicyIssueDate: values?.minPolicyIssueDate || "",
    maxPolicyIssueDate: values?.maxPolicyIssueDate || "",
    minInvoiceIssueDate: values?.minInvoiceIssueDate || "",
    maxInvoiceIssueDate: values?.maxInvoiceIssueDate || "",
    minSchedulePaymentDate: values?.minSchedulePaymentDate || "",
    maxSchedulePaymentDate: values?.maxSchedulePaymentDate || "",
    minPaymentDate: values?.minPaymentDate || "",
    maxPaymentDate: values?.maxPaymentDate || "",
    certificateNumber: values?.certificateNumber || "",
    status: "0",
    maxCount: String(count),
  });
  return `/invoice/page/${page}?${params.toString()}`;
};

const headers = [
  { label: "" },
  {
    label: (
      <>
        <div>Invoice Number</div>
        <div>Placing Slip Number</div>
        <div>Policy Number</div>
        <div>Reference Number</div>
      </>
    ),
    style: { minWidth: 210 },
  },
  { label: "Product Code" },
  {
    label: (
      <>
        Company Name <br />
        Insured <br />
        Insurer
      </>
    ),
    style: { minWidth: 250 },
  },
  {
    label: (
      <>
        Policy Issue Date <br />
        Invoice Issue Date <br />
        Schedule Payment Date <br />
        Payment Date
      </>
    ),
    style: { minWidth: 210 },
  },
  {
    label: (
      <>
        Sum Insured <br />
        Premium
      </>
    ),
    style: { minWidth: 140 },
  },
  { label: "Action" },
];

export default function WaitingInvoice({ navigation }) {
  const scroll = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const { invoiceNumbers, invoiceTotal } = useSelector(
    (state) => state.summary
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [rows, setRows] = useState([]);
  const [values, setValues] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPage, setTotalPage] = useState(0);
  const [allChecked, setAllChecked] = useState(false);

  const fetchInvoices = (
    nextPage = 1,
    nextValues = values,
    nextCount = rowsPerPage
  ) => {
    setLoading(true);
    axiosGet({
      url: invoiceUrl(nextPage, nextValues, nextCount),
      callback: (res) => {
        const data = res.data.data;
        setInvoices(data);
        setRows(
          data.map((invoice) => ({
            ...invoice,
            checked: invoiceNumbers.some(
              (selected) => selected.id === invoice.id
            ),
          }))
        );
        setPage(res.data.page - 1);
        setTotalPage(res.data.totalPage);
        setAllChecked(false);
        setLoading(false);
      },
      errorCallback: (err) => {
        console.log(err);
        setLoading(false);
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        history.push("/404");
      },
    });
  };

  useEffect(() => {
    fetchInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSelection = (nextRows) => {
    const pageIds = new Set(nextRows.map((row) => row.id));
    const selected = [
      ...invoiceNumbers.filter((invoice) => !pageIds.has(invoice.id)),
      ...nextRows.filter((row) => row.checked),
    ];
    dispatch(setInvoiceNumbers(selected));
    dispatch(
      setInvoiceTotal(
        selected.reduce((total, invoice) => total + Number(invoice.premium), 0)
      )
    );
  };
  const handleCheckbox = (event, invoice) => {
    const nextRows = rows.map((row) =>
      row.id === invoice.id ? { ...row, checked: event.target.checked } : row
    );
    setRows(nextRows);
    setAllChecked(nextRows.every((row) => row.checked));
    updateSelection(nextRows);
  };
  const handleAllChecked = (event) => {
    const nextRows = rows.map((row) => ({
      ...row,
      checked: event.target.checked,
    }));
    setRows(nextRows);
    setAllChecked(event.target.checked);
    updateSelection(nextRows);
  };
  const handleSearch = (searchValues) => {
    setValues(searchValues);
    fetchInvoices(1, searchValues);
  };
  const handleReset = () => {
    setValues(null);
    fetchInvoices(1, null);
  };
  const handlePageChange = ({ selected }) => {
    fetchInvoices(selected + 1);
    scroll.current?.scrollIntoView();
  };
  const handleRowsPerPageChange = (nextCount) => {
    const count = Number(nextCount);
    setRowsPerPage(count);
    fetchInvoices(1, values, count);
    scroll.current?.scrollIntoView();
  };
  const handleCheckout = () => {
    const numbers = invoiceNumbers.map((invoice) => invoice.invoiceNumber);
    if (!numbers.length) return;
    window.scrollTo(0, 0);
    setCheckoutLoading(true);
    axiosGet({
      url: `/invoice/summary-paid?invoiceNumbers=${numbers}`,
      callback: (res) => {
        dispatch(
          setDataSummary({
            invoiceList: res.data.invoiceList,
            totalAmount: res.data.totalAmount,
          })
        );
        history.push("/summary");
      },
      errorCallback: (err) => {
        console.log(err);
        setCheckoutLoading(false);
      },
    });
  };

  if (checkoutLoading) return <Loading />;
  return (
    <>
      <div className="border rounded mt-5">
        <MDBContainer
          className="px-4 py-2"
          onClick={() => setOpen(!open)}
          style={{ cursor: "pointer" }}
        >
          <MDBRow>
            <MDBCol size="6" middle>
              <p className="mb-0">Search</p>
            </MDBCol>
            <MDBCol size="6" className="text-right">
              <MDBIcon icon={open ? "angle-up" : "angle-down"} size="lg" />
            </MDBCol>
          </MDBRow>
        </MDBContainer>
        {open && <WaitingForm onSearch={handleSearch} onReset={handleReset} />}
      </div>
      {navigation}
      <div className="mt-5" ref={scroll}>
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="p-4 border rounded text-grey">
              {invoices.length > 0 && (
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id={`tableCheckboxAll${page + 1}`}
                    onChange={handleAllChecked}
                    checked={allChecked}
                  />
                  <label
                    className="custom-control-label font-weight-bold"
                    htmlFor={`tableCheckboxAll${page + 1}`}
                  >
                    Choose All
                  </label>
                </div>
              )}
              <DataTable
                invoices={rows}
                active="waiting"
                onChecked={handleCheckbox}
                allChecked={allChecked}
                headers={headers}
              />
              {!invoices.length && (
                <div className="font-weight-bold text-center">NO DATA</div>
              )}
            </div>
            <div className="mt-5 d-flex align-items-end justify-content-between">
              <div>
                <p>Rows per page</p>
                <select
                  className="browser-default custom-select"
                  style={{ width: 75 }}
                  value={rowsPerPage}
                  onChange={(event) =>
                    handleRowsPerPageChange(event.target.value)
                  }
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              {totalPage > 1 && (
                <ReactPaginate
                  previousLabel={<MDBIcon icon="chevron-left" />}
                  nextLabel={<MDBIcon icon="chevron-right" />}
                  breakLabel="..."
                  pageCount={totalPage}
                  pageRangeDisplayed={2}
                  marginPagesDisplayed={1}
                  forcePage={page}
                  onPageChange={handlePageChange}
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  containerClassName="pagination"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  activeClassName="active"
                />
              )}
            </div>
            <div className="mt-5 total-invoice">
              <div>
                <p>Total Invoice</p>
                <p>{invoiceNumbers.length}</p>
              </div>
              <div>
                <p>Total Payment</p>
                <p>{`(IDR) ${handleCurrency(invoiceTotal)}`}</p>
              </div>
            </div>
          </>
        )}
        <div className="mt-5 d-flex justify-content-end">
          <Button text="CHECKOUT" onClick={handleCheckout} />
        </div>
      </div>
    </>
  );
}
